<template>
    <div style="overflow: hidden; width: 560px;">

      <div class="field-group q-mt-sm">
        <div class="field-group__row no-wrap items-baseline justify-between">
          <div class="bold-name q-mr-xs">
            {{ $t('payment_type') }}<span v-if="theModel.paymentType == null || theModel.paymentType === undefined" class="required-asterisk">*</span><span v-else>:</span>
          </div>
          <q-select
            flat
            v-model="theModel.paymentType"
            style="min-width: 150px; padding: 0 !important;"
            class="thin-input"
            dropdown-icon="expand_more"
            fill-input
            :bottom-slots="false"
            :disable="incomingOnly"
            dense
            outlined
            input-debounce="500"
            emit-value
            map-options
            option-label="Name"
            option-value="Id"
            ref="from_select"
            :options="typeOptions"
          />
        </div>
      </div>
      
      <div class="field-group q-mt-sm">
        <div class="field-group__row no-wrap items-baseline justify-between">
          <div class="bold-name q-mr-xs">
            {{ $t('amount') }}<span v-if="Number(theModel.paymentAmount) <= 0 || isNaN(Number(theModel.paymentAmount))" class="required-asterisk">*</span><span v-else>:</span>
          </div>
          <native-input
            ref="amount_input"
            style="min-width: 150px"
            v-model="theModel.paymentAmount"
            input-type="number"
            :edit-on-click="true"
            :show-border="true"
            :min-number-value="0"
          />
        </div>
      </div>

      <div class="field-group q-mt-sm">
        <div class="field-group__row no-wrap items-baseline justify-between">
          <div class="bold-name q-mr-xs">
            {{ $t('number') }}<span v-if="!String(theModel.paymentNumber ?? '').trim()" class="required-asterisk">*</span><span v-else>:</span>
          </div>
          <native-input
            style="min-width: 150px"
            ref="number_input"
            :show-border="true"
            :edit-on-click="true"
            v-model="theModel.paymentNumber"
          />
        </div>
      </div>

      <div class="field-group q-mt-sm">
        <div class="field-group__row no-wrap items-baseline justify-between">
          <div class="bold-name q-mr-xs">
            {{ $t('payment_date') }}<span v-if="!theModel.paymentDate" class="required-asterisk">*</span><span v-else>:</span>
          </div>
          <VueDatePicker utc="preserve" timezone="Europe/Moscow" :locale="$i18n.locale" :style="'width: 150px; min-width: 150px; max-width: 150px;'" :teleport="true"
            v-model="theModel.paymentDate" :enable-time-picker="false"
            :clearable="false" :required="true" :format="formatDate" :text-input="textInputOptions" />
        </div>
      </div>

      <div class="field-group q-mt-sm">
        <div class="field-group__row no-wrap items-baseline justify-between">
          <div class="bold-name q-mr-xs">
            {{ $t('currency') }}<span v-if="!String(theModel.currencyName ?? '').trim()" class="required-asterisk">*</span><span v-else>:</span>
          </div>
          <native-input
            style="min-width: 150px"
            ref="currency_name_input"
            :show-border="true"
            :edit-on-click="true"
            v-model="theModel.currencyName"
          />
        </div>
      </div>

      <div class="field-group q-mt-sm">
        <div class="field-group__row no-wrap justify-between">
          <div class="bold-name q-mr-xs">
            {{ $t('currency_rate') }}:
          </div>
          <native-input
            style="min-width: 150px"
            ref="currency_input"
            v-model="theModel.paymentCurrency"
            input-type="number"
            :edit-on-click="true"
            :show-border="true"
            :min-number-value="0"
          />
        </div>
      </div>

      <div class="field-group q-mt-sm">
          <div class="bold-name q-mr-xs q-mt-xs">
            {{ $t('payment_comment') }}:
          </div>
          <native-input
            style="max-width: 100%; min-width: 100%;"
            ref="comment_input"
            v-model="theModel.paymentNote"
            :edit-on-click="true"
            :show-border="true"
          />
        </div>

    </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from "vue-facing-decorator";
import _ from "lodash";
import NativeInput from "../../../common/basic/NativeInput.vue";
import { BillType } from "../../../../models/etc/types";

export interface PaymentStructure {
  paymentType: BillType;
  paymentAmount: number;
  paymentNumber: string;
  paymentDate: Date;
  currencyName: string;
  paymentCurrency: number;
  paymentNote: string;
}

@Component({
  components: { NativeInput },
  emits: ["close", "update:modelValue"],
})
export default class PaymentCreateComponent extends Vue {
  @Prop({ default: undefined })
  modelValue!: PaymentStructure
  @Prop({ default: true })
  incomingOnly!: boolean
  
  created() {}

  mounted() {}

  get theModel() {
    return this.modelValue
  }

  get textInputOptions() {
    return {
      enterSubmit: true,
      tabSubmit: true,
      openMenu: 'toggle',
      rangeSeparator: '-',
      selectOnFocus: false,
      format: 'dd.MM.yyyy'
    }
  }

  @Watch('modelValue', { deep: true })
  model_changed(new_value: PaymentStructure) {
    this.$emit('update:modelValue', new_value)
  }

  formatDate (date: Date): string {
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();

    return `${String(day).padStart(2, '0')}.${String(month).padStart(2, '0')}.${year}`;
  }

  get typeOptions() {
    return [
      {
        Name: this.$t('incoming'),
        Id: BillType.Incoming,
      },
      {
        Name: this.$t('outgoing'),
        Id: BillType.Outgoing,
      },
    ];
  }

  close() {
    this.$emit("close", null);
  }
}
</script>

<style lang="scss" scoped>
.required-asterisk {
  color: red;
  margin-left: 0.2em;
  margin-right: 0.2em;
}
</style>
